// Central SEO config + JSON-LD builders.
//
// SITE URL resolution order (most specific wins):
//   1. REACT_APP_SITE_URL — explicit override (set in /app/frontend/.env if needed)
//   2. window.location.origin — runtime origin (preview, default Emergent URL, or
//      custom domain like https://carsavr.com once linked)
//   3. https://carsavr.com — final fallback for SSR/build-time
//
// This means schema.org JSON-LD, canonical tags, OG URLs, and breadcrumb items
// all stay consistent with whichever hostname the page is served from — no
// hardcoded "carsavr.com" leaking into preview deployments and no stale URLs
// when you connect a new custom domain.
import { AUTHORS, getReviewerForArticle } from "./authors";

export const SITE_NAME = "CarSavr";

// Iter-275.AV (Phase 4 #14) — Build-time freshness anchor for FAQ blocks
// that don't have an associated article.updated_at (pillar pages,
// calculator pages, evergreen hubs). Bumped on each editorial refresh
// so Google's rich-results inspector sees a recent dateModified.
export const FAQ_LAST_REVIEWED_AT = "2026-02-01";

export const getSiteUrl = () => {
  const envUrl = process.env.REACT_APP_SITE_URL;
  if (envUrl) return envUrl.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://carsavr.com";
};

// Iter-239 — Build a rich Person JSON-LD from an AUTHORS registry entry.
// Used for both Article.author and Article.reviewedBy so Google's EEAT
// knowledge graph sees the SAME @id across every surface the editor
// touches (the article, the /editors/<slug> bio, the founder block in
// Organization schema). All fields are sourced from the registry —
// nothing fabricated, no fake credentials emitted when fields are null.
const personLdFromAuthor = (author, baseUrl) => {
  if (!author || !author.slug) return null;
  const url = baseUrl || getSiteUrl();
  const editorUrl = `${url}${author.href || `/editors/${author.slug}`}`;
  const sameAs = [
    author.linkedin,
    author.twitter
      ? (author.twitter.startsWith("http")
          ? author.twitter
          : `https://x.com/${author.twitter.replace(/^@/, "")}`)
      : null,
  ].filter(Boolean);
  const person = {
    "@type": "Person",
    "@id": `${editorUrl}#person`,
    name: author.name,
    jobTitle: author.title || author.role,
    url: editorUrl,
    worksFor: { "@type": "Organization", name: SITE_NAME, url },
  };
  if (author.photo) person.image = `${url}${author.photo}`;
  if (author.bio) person.description = author.bio;
  if (Array.isArray(author.expertise) && author.expertise.length > 0) {
    person.knowsAbout = author.expertise;
  }
  if (sameAs.length > 0) person.sameAs = sameAs;
  return person;
};

// Branded 1200×630 Open Graph card served from /public/og-default.png.
// Generated by /app/scripts/generate_og_image.py. Re-run that script after
// editing brand colors / copy to regenerate the PNG.
// We resolve the URL at render time so it follows whichever hostname is
// serving the page (preview, default Emergent URL, or custom domain).
export const getDefaultOgImage = () => `${getSiteUrl()}/og-default.png`;

// Legacy export kept for back-compat. Same behavior — uses runtime origin.
export const FALLBACK_OG_IMAGE = getDefaultOgImage();

// Iter-419 — Title budget for programmatic templates.
//
// Bing flags "Title too long" above ~60 rendered characters, and <SEO>
// appends " | CarSavr" (10 chars), so a page-supplied title has a
// 50-character budget. Programmatic templates pass their candidates
// longest-first; `fitTitle` returns the first one that fits, and falls
// back to a word-boundary trim of the shortest candidate so a freak
// long input (a very long carrier or model name) can never blow the
// budget on a newly generated page.
export const TITLE_BUDGET = 50;

export const fitTitle = (...candidates) => {
  const list = candidates.filter(Boolean);
  for (const candidate of list) {
    if (candidate.length <= TITLE_BUDGET) return candidate;
  }
  const shortest = list[list.length - 1] || SITE_NAME;
  let out = "";
  for (const word of shortest.split(" ")) {
    if (`${out} ${word}`.trim().length > TITLE_BUDGET) break;
    out = `${out} ${word}`.trim();
  }
  return out || shortest.slice(0, TITLE_BUDGET);
};

export const absUrl = (path = "/") => {
  if (!path) return getSiteUrl();
  if (typeof path === "string" && path.startsWith("http")) return path;
  const base = getSiteUrl();
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

// --- JSON-LD builders ---
// Built lazily (each call invokes getSiteUrl) so they always reflect the
// current runtime origin.
export const organizationLd = () => {
  const url = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url,
    logo: `${url}/icon-512.png`,
    // sameAs declares verified external profiles. Each link must point to a
    // page CarSavr actually owns/controls — Google penalizes Organization
    // markup that lists profiles the org doesn't operate. Iter-190.E:
    // updated to the live @carsavr handles across Instagram, Facebook,
    // X, Pinterest, YouTube, and TikTok. Strengthens Knowledge-Graph
    // entity recognition + AI Overviews attribution.
    sameAs: [
      `${url}/about`,
      `${url}/about#editorial-leadership`,
      "https://www.instagram.com/carsavr",
      "https://www.facebook.com/share/1bU2cwW4PL/",
      "https://x.com/carsavr",
      "https://pin.it/7KAagrIdQ",
      "https://youtube.com/@carsavr",
      "https://www.tiktok.com/@carsavr.com",
    ],
    description:
      "CarSavr helps drivers compare auto insurance, refinance loans, and extended warranties .",
    // Surfacing a real, named founder strengthens E-E-A-T for YMYL (Your
    // Money, Your Life) ranking on finance queries. Use only facts the
    // founder has confirmed publicly — do NOT add fabricated credentials.
    founder: {
      "@type": "Person",
      name: "Michael Ecke",
      jobTitle: "Founder & Editor",
      image: `${url}/team/michael-ecke.jpg`,
      worksFor: { "@type": "Organization", name: SITE_NAME, url },
    },
  };
};

export const websiteLd = () => {
  const url = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url,
    potentialAction: {
      "@type": "SearchAction",
      target: `${url}/guides?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
};

// Iter-275.AV (Phase 4 #14) — FAQPage dateModified.
// Google Quality Rater + rich-results inspectors flag FAQPage blocks
// without a freshness signal as "stale-looking" content. We accept an
// optional `dateModified` (ISO 8601) and emit it when present so the
// FAQ block surfaces alongside the article's last-update timestamp.
// Callers with an `article.updated_at` (SeoGuideShell, GuidePage,
// ArticlePage, CalculatorPage) wire it through automatically. Pillar
// pages with hardcoded FAQ arrays should pass a build-time constant.
export const faqPageLd = (faqs, options = {}) => {
  const block = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  if (options.dateModified) block.dateModified = options.dateModified;
  if (options.datePublished) block.datePublished = options.datePublished;
  return block;
};

// Iter-130 — Speakable-enabled FAQPage.
//
// Wraps the standard FAQPage with a sibling `speakable` annotation
// (SpeakableSpecification) so Google Assistant + AI Overviews can read
// the top answers out loud as a definitive source. The cssSelector
// targets `[data-speakable="faq-answer"]` elements emitted by the
// `<FAQ>` component (see iter-130 update).
//
// Limit defaults to the top 3 FAQs — Google's own docs recommend
// emitting Speakable only on succinct, factual content. Beyond ~3
// answers the markup loses signal value.
//
// Pass-through for the existing FAQPage shape so callers can swap
// `faqPageLd(FAQS)` → `speakableFaqPageLd(FAQS)` with zero behavioural
// regression for clients that don't read Speakable.
export const speakableFaqPageLd = (faqs, options = {}) => {
  const topN = Math.max(1, Math.min(options.limit || 3, faqs.length));
  // Generate selector list — `[data-speakable="faq-question-N"]` +
  // `[data-speakable="faq-answer-N"]` per top FAQ. Concrete attribute
  // values let multiple FAQPage blocks coexist on the same page
  // (e.g., when a state page + the site-wide FAQ both render).
  const cssSelector = [];
  for (let i = 0; i < topN; i++) {
    cssSelector.push(`[data-speakable="faq-question-${i}"]`);
    cssSelector.push(`[data-speakable="faq-answer-${i}"]`);
  }
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector,
    },
    // Iter-275.AV (Phase 4 #14) — FAQPage dateModified.
    ...(options.dateModified ? { dateModified: options.dateModified } : {}),
    ...(options.datePublished ? { datePublished: options.datePublished } : {}),
  };
};

export const articleLd = (article) => {
  const url = getSiteUrl();
  // YMYL: surface dateModified (and dateReviewed when distinct) so Google's
  // finance-content ranking sees an explicit freshness signal. Falls back to
  // datePublished when no separate update/review timestamp exists.
  const reviewed =
    article.last_reviewed_at || article.updated_at || article.published_at;
  // Iter-196.VV — Per-vertical reviewer attribution. Insurance content
  // routes to Abigail Murray (active personal-lines underwriter at
  // Progressive); all other verticals route to Michael Ecke. Falls back
  // to Michael when the article carries no cluster/category. The
  // resolver also honors an explicit `article.reviewer` slug if the
  // admin set one on the row.
  //
  // Heavy lift: this is the E-E-A-T signal Google's Helpful-Content +
  // AI-Overview rankings reward most on YMYL pages. A credentialed
  // practitioner reviewing content adjacent to their day job (e.g. an
  // insurance underwriter reviewing insurance comparisons) materially
  // outperforms a generic "Editorial Team" attribution.
  const reviewerForVertical = getReviewerForArticle(article);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: [article.image_url],
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    // YMYL: schema.org/Article doesn't define dateReviewed as a top-level
    // property, so we surface it under `reviewedBy` — a published extension
    // that Google's structured-data testing tool accepts and that maps cleanly
    // to the visible "Reviewed by X on Y" stamp in the byline.
    reviewedBy: {
      "@type": "Person",
      name: reviewerForVertical.name,
      jobTitle: reviewerForVertical.title || reviewerForVertical.role,
      url: `${url}${reviewerForVertical.href || `/editors/${reviewerForVertical.slug}`}`,
      image: reviewerForVertical.photo
        ? `${url}${reviewerForVertical.photo}`
        : undefined,
      // Iter-196.VV — Surface the reviewer's @id so search engines can
      // link this attribution back to the same Person entity that's
      // published on /editors/<slug> (with full sameAs links, worksFor
      // org, expertise list, etc.). This is what closes the knowledge-
      // graph loop: same @id across every article a reviewer touches.
      "@id": `${url}/editors/${reviewerForVertical.slug}#person`,
    },
    // Google's Article rich-result documentation prefers a Person author.
    // Iter-119 — when the byline field is missing OR carries the legacy
    // "CarSavr Editorial Team" placeholder, attribute to Michael Ecke
    // (the real named founder/editor — see /editors/michael-ecke) so the
    // Article schema points to a verifiable Person, never to a non-entity.
    //
    // Iter-239 — Use the AUTHORS registry to emit a *rich* Person object
    // with @id (knowledge-graph linkable), worksFor, image, description,
    // and knowsAbout — same surface as the /editors/<slug> Person LD.
    // Mirrors Google's "author of the article = the editor with a real
    // public bio + verifiable affiliation" EEAT signal.
    author: (() => {
      const legacyTeam =
        !article.byline ||
        (typeof article.byline === "string" &&
          article.byline.trim().toLowerCase() === "carsavr editorial team");
      if (legacyTeam) {
        // Default named author = Michael Ecke. Reviewer can still differ
        // per cluster (e.g. insurance reviewer Abigail Murray).
        return personLdFromAuthor(AUTHORS.michael, url) || {
          "@type": "Person",
          name: "Michael Ecke",
          jobTitle: "Founder & Editor",
          url: `${url}/editors/michael-ecke`,
        };
      }
      // Custom byline string — try to resolve to a known editor first
      // so we still emit the rich Person LD; otherwise fall back to a
      // minimal stub pointing to the public bio.
      const candidate = Object.values(AUTHORS).find(
        (a) =>
          a.name && a.name.toLowerCase() === article.byline.toLowerCase(),
      );
      if (candidate) {
        return personLdFromAuthor(candidate, url);
      }
      return { "@type": "Person", name: article.byline, url: `${url}/about` };
    })(),
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${url}/icon-512.png` },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": absUrl(`/guides/${article.slug}`),
    },
    articleSection: article.category,
    // Free-form `dateReviewed` for crawlers that read the extension (Bing
    // emits it; Google reads-but-doesn't-officially-rank on it yet). The
    // visible stamp in the byline is what carries the real YMYL weight.
    dateReviewed: reviewed,
  };
};

// Iter-131 — Article schema with embedded SpeakableSpecification (S4).
//
// Wraps the existing `articleLd()` output with a sibling `speakable`
// node whose cssSelector list targets `data-speakable="article-q-N"`
// attributes the Markdown component emits when it spots a question-style
// H2.
//
// Why question-only (not also `article-a-N`):
//   * Article bodies on CarSavr get split into 2-3 chunks for inline-CTA
//     placement. Each chunk renders through its own Markdown instance,
//     so line-positional or sibling-order matching of "the paragraph
//     after this H2" breaks across the chunk boundary.
//   * Slug-keyed matching is durable across chunks for headings (each
//     H2's slug ID is stable) but there's no stable slug for the
//     answer paragraph/list/table that follows.
//   * Google's Speakable spec explicitly accepts question-only markup
//     — voice search + AI Overviews will read the question text and the
//     adjacent body content naturally; tagging the answer is a nice-
//     to-have, not a requirement.
//
// Falls back to the plain Article schema (no Speakable sibling) when
// no question-style H2 is detected — better than emitting selectors
// that point at nothing, which would fail Google's validation.
export const speakableArticleLd = (article, speakableHeadings = []) => {
  const base = articleLd(article);
  const cssSelector = speakableHeadings.map(
    (h) => `[data-speakable="article-q-${h.index}"]`,
  );
  // Iter-240 — Always include the AI Quick Answer block in the speakable
  // selector when the article ships one. Google AI Overviews + Perplexity
  // preferentially quote whatever the page declares as speakable, so this
  // is the single most important hook on the page.
  if (article && article.quick_answer) {
    cssSelector.push('[data-testid="article-quick-answer"]');
  }
  if (cssSelector.length === 0) return base;
  return {
    ...base,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector,
    },
  };
};

// SoftwareApplication / WebApplication / FinancialProduct schema for
// calculator pages. Google can show app-like rich results for these
// (free price tag). Pass the calculator's name + URL + 1-sentence
// description.
//
// Iter-100 — removed the previously-hardcoded `aggregateRating` ({ ratingValue:
// "4.8", ratingCount: "2100" }). CarSavr does not (yet) run a real user-rating
// system for its calculators, and Google's structured-data guidelines
// explicitly penalize Review/AggregateRating markup that doesn't reflect
// real reviews. The schema below is still rich enough to qualify for
// SoftwareApplication rich results — we just don't emit a fabricated star
// average. If/when we ship a real rating widget on calculator pages, add
// the aggregateRating back here using live data.
//
// Iter-192.A (Competitive Audit Phase 2.3.B / 1.E) — emit BOTH
// `WebApplication` and `SoftwareApplication` simultaneously via a
// `@type` array. Schema.org explicitly supports this — and Google's
// Calculator-rich-result tier specifically reads the WebApplication
// flavor (NerdWallet uses this combo on every calculator page).
// SoftwareApplication is kept for backward compat with older crawlers
// + non-Google search engines (Bing reads it preferentially).
export const calculatorAppLd = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": ["WebApplication", "SoftwareApplication"],
  name,
  applicationCategory: "FinanceApplication",
  browserRequirements: "Requires JavaScript. Works in modern browsers.",
  operatingSystem: "Web",
  description,
  url: absUrl(path),
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: (items || [])
    // Iter-195.E (GSC fix) — Defensive coercion. Several call-sites
    // historically passed the in-page chrome shape `{ label, to }`
    // (e.g. StatePillarHub's `view.breadcrumb` array) which produced
    // `name: undefined` and triggered Google Search Console's
    // "Either 'name' or 'item.name' should be specified" error on
    // /auto-insurance/states + /auto-loans/states. Accept both
    // shapes here AND drop any entry that still has no usable label,
    // so a single stale call-site can't break the whole JSON-LD block.
    .map((it) => ({
      name: (it.name || it.label || "").toString().trim(),
      // Accept either `path` (preferred) or `url` (legacy callsite shape)
      // or `to` (chrome shape from StatePillarHub). Falling back to "/"
      // so we never emit `item: undefined` which would make the whole
      // breadcrumb block invalid in Google's eyes.
      target: it.path || it.url || it.to || "/",
    }))
    // Drop any entry that still has no usable name (e.g. callsite
    // passed `{ to: "/foo" }` with no label at all). Google requires
    // every itemListElement to carry a name.
    .filter((it) => Boolean(it.name))
    // Re-number positions AFTER filtering so the surviving items
    // form a continuous 1,2,3 sequence (schema requirement).
    .map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: absUrl(it.target),
    })),
});

// Builds a single Product node for an editorially-rated lender / insurer.
// Wrapped inside an ItemList on roundup pages so Google can show ⭐ ratings
// in SERP results (the canonical "Best of" structured-data pattern).
//
// Skips emitting AggregateRating if the rating is missing OR the row has no
// editor_review (Google penalizes Review markup without substantive copy).
const _lenderCategoryName = (c) =>
  ({
    "auto-loan": "Auto Loan",
    refinance: "Auto Loan Refinance",
    insurance: "Auto Insurance",
    warranty: "Extended Warranty",
  }[c] || "Auto Financial Product");

export const lenderProductLd = (lender, pagePath) => {
  if (!lender?.rating || !lender?.editor_review) return null;
  const url = getSiteUrl();
  // Iter-191.B (SEO Audit 6.B) — Extended warranties are services, not
  // products, per schema.org's domain definitions. Emit @type: Service
  // for the warranty category so Google's structured-data validator
  // doesn't suppress the rich result (Product without a price block on
  // a service offering frequently fails Google's stricter 2025+ Product
  // rich-result eligibility tier). Loan + insurance + refinance remain
  // Product since FinancialProduct extends Product and these are
  // commonly indexed as products in SERP rich results.
  const isService = lender.category === "warranty";
  const baseType = isService ? "Service" : "Product";
  return {
    "@context": "https://schema.org",
    "@type": baseType,
    name: `${lender.name} — ${_lenderCategoryName(lender.category)}`,
    description: lender.editor_review,
    ...(isService
      ? {
          provider: { "@type": "Organization", name: lender.name },
          serviceType: _lenderCategoryName(lender.category),
          areaServed: { "@type": "Country", name: "United States" },
        }
      : {
          brand: { "@type": "Brand", name: lender.name },
          category: _lenderCategoryName(lender.category),
        }),
    url: absUrl(pagePath),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: lender.rating.toFixed(1),
      bestRating: "5",
      worstRating: "1",
      ratingCount: "1",
      reviewCount: "1",
    },
    review: {
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: lender.rating.toFixed(1),
        bestRating: "5",
      },
      author: { "@type": "Organization", name: SITE_NAME, url },
      reviewBody: lender.editor_review,
    },
  };
};

// Wraps a list of lender Product nodes in an ItemList for "Best of" pages.
export const lenderItemListLd = (lenders, pagePath, name) => {
  const items = (lenders || [])
    .map((l, i) => {
      const product = lenderProductLd(l, pagePath);
      if (!product) return null;
      return { "@type": "ListItem", position: i + 1, item: product };
    })
    .filter(Boolean);
  if (items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: items.length,
    itemListElement: items,
  };
};

// Iter-60: HowTo schema for calculator pages. Google can surface step-by-step
// rich results when a tool has a clear `n inputs → result` flow. Pass an array
// of step objects { name, text } to a CalculatorPage and we wrap them in a
// HowTo node alongside the existing SoftwareApplication + FAQPage schema.
//
// Why HowTo (not just SoftwareApplication)?
//   • SoftwareApplication tells search engines "this is a tool".
//   • HowTo tells search engines "use the tool like this" — and Google
//     specifically shows HowTo carousels for "how to calculate X" queries
//     that map directly to our calculator titles.
//
// supply / tool fields intentionally omitted — those are for physical projects
// (cooking, DIY) and trigger validation warnings on financial calculators.
export const howToLd = ({ name, description, path, steps }) => {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    totalTime: "PT1M",
    url: absUrl(path),
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: `${absUrl(path)}#step-${i + 1}`,
    })),
  };
};

// Iter-60: ItemList builder for landing/hub pages that surface a curated set
// of links to deeper comparison/decision content. Distinct from
// `lenderItemListLd` (which wraps Product nodes with AggregateRating).
//
// Use this on:
//   • /compare — list of decision head-to-heads + provider showdowns
//   • /calculators — list of available calculators
//   • /guides — list of guides (if/when we want a dedicated CollectionPage)
//
// items: [{ name, path, description? }]
export const collectionItemListLd = ({ name, items }) => {
  const list = (items || []).filter((it) => it && it.name && it.path);
  if (list.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: list.length,
    itemListElement: list.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: absUrl(it.path),
      ...(it.description ? { description: it.description } : {}),
    })),
  };
};

// Iter-275.AY (Phase 6 #12 — Production Readiness Audit).
// OfferCatalog wraps a list of services/products that an
// Organization offers. On /compare we publish an OfferCatalog
// of the provider showdowns (auto-loan / refinance / insurance
// pair pages) so Google's structured-data graph recognises the
// page as a catalog surface, not just a flat ItemList. This is
// the schema.org pattern that powers rich "Top picks" carousels
// in AI Overviews + Discover for service comparisons.
//
// `items` is a list of `{ name, path, category, description }`
// — same shape used by collectionItemListLd so callers can pass
// the spread of arrays they already build.
export const offerCatalogLd = ({ name, items, providerName = "CarSavr" }) => {
  const list = (items || []).filter((it) => it && it.name && it.path);
  if (list.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name,
    provider: {
      "@type": "Organization",
      name: providerName,
      url: getSiteUrl(),
    },
    numberOfItems: list.length,
    itemListElement: list.map((it, i) => ({
      "@type": "Offer",
      position: i + 1,
      url: absUrl(it.path),
      itemOffered: {
        "@type": "Service",
        name: it.name,
        ...(it.description ? { description: it.description } : {}),
        ...(it.category ? { category: it.category } : {}),
        url: absUrl(it.path),
      },
    })),
  };
};

// Iter-196.Y (F-SEO-09 from audit) — Speakable schema for the
// /<vs>/ "Editor verdict" card.
//
// The verdict summary is the single most AI-Overview-friendly sentence
// on the entire comparison page: a definitive answer to the head-to-head
// question. Wrapping it in a SpeakableSpecification node lets Google
// Assistant + AI Overviews lift the answer verbatim as the canonical
// source. Pair this with the `data-speakable="verdict-summary"` attribute
// the ComparisonVerdictCard emits on the summary paragraph so the
// cssSelector resolves to a single, stable target.
//
// `summary` is a courtesy field — Google reads the rendered DOM, but the
// schema also exposes the answer text under `mainContentOfPage` so AI
// agents that can't crawl JS (Claude indexer, Perplexity index workers)
// see the same one-sentence verdict.
export const speakableComparisonVerdictLd = ({ slug, summary }) => {
  if (!summary) return null;
  return {
    "@context": "https://schema.org",
    "@type": "WebPageElement",
    name: "Editor verdict",
    description: summary,
    url: absUrl(`/${slug}`),
    isPartOf: { "@type": "WebPage", "@id": absUrl(`/${slug}`) },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ['[data-speakable="verdict-summary"]'],
    },
  };
};

// Back-compat: some old imports may still reference SITE_URL as a string.
// Resolve it at module load via the same logic (single snapshot).
export const SITE_URL = getSiteUrl();
